/* Copyright (c) 2016-2023 Planticle Pty Ltd */
"use strict";

import birdata from "../api.js";
import ko from "knockout";
import _ from "underscore";
import validate from "../util/validate.js";
import fieldManager from "../util/fields.js";
import util from "../util/util.js";
import googleMaps from "../util/google-maps.js";
import MediaUploaderViewModel from "../ui/shared/media-uploader.js";

function isZero(x) {
	return ("" + x).trim() === "0";
}

var program = {
	code: "egnb",
	name: "East Gippsland Nest Box Project",
	group: {
		name: "Nest Box / Hollow Monitoring",
		code: "nb"
	},
	description: `
	<p>This Birdata module collects the results of monitoring for birds and other animals that are residing within natural hollows, augmented hollows and nest boxes.  Specialised survey methods are required for this monitoring technique, taking into account a range of ethical, conservation, animal welfare and privacy considerations. Its use is restricted to authorised Birdata users that have been trained and registered to participate in one or more ‘hollow-monitoring’ projects coordinated by BirdLife Australia and affiliated organisations.  It has been developed as part of a Landcare Led Bushfire Recovery project, that was supported by the Australian Government's Bushfire Recovery Program for Wildlife and their Habitats after the 2019/20 Black Summer fires.</p>
	<p><em>Photo credit: Crimson Rosella in nest box photographed by Darilyn Goldsmith</em></p>`,
	canParticipate: function(user) {
		return user.is_administrator || _.any(user.permissions, function(p) {
			return p.type == "program" && p.program == "egnb";
		});
	},
	canFilterBy: function(user) {
		return this.canParticipate(user);
	},
	load: function() {
		return birdata.lookup().then(lookups => {
			program.options = lookups;
		});
	},
	fields: function(survey) {
		var fields = [
			{
				name: "siteDetailsAccurate",
				label: "Is the description below of the nest box or hollow accurate?",
				type: "boolean",
				style: "radio",
				defaultValue: null,
				validate: "required"
			},
			{
				name: "siteDetailsCorrections",
				label: "Provide further details about the inaccuracy of the information provided below.",
				type: "text",
				multiline: true,
				validate: "required",
				hidden: function() { return fields.siteDetailsAccurate() !== false; }
			},
			{
				name: "monitoringMethod",
				type: "options",
				validate: "required",
				options: program.options.nest_box_hollow_monitoring_method
			},
			{
				name: "dataLoggerPresent",
				label: "Is there a data logger or inbuilt wildlife camera associated with the box/hollow?",
				type: "boolean",
				style: "radio",
				validate: "required",
				defaultValue: null
			},
			{
				name: "nestBoxHollowPhotos",
				label: "Photo(s) of nest box / hollow",
				type: "custom",
				isArrayType: true,
				key: 'nest_box_hollow_photo_uuids',
				to_json: function(value) {
					return value().map(x => x.uuid()).filter(x => x);
				},
				from_json: function(json) {
					if(json) {
						return json.map(uuid => MediaUploaderViewModel.createMediaModel(null, uuid));
					} else {
						return [];
					}
				},
				formattedValue: function(val) {
					return util.formatQuantity(val.length, "item");
				}
			}
		].map(fieldManager.field);

		fields = util.arrayToMap(fields, function(f) { return f.field.name; });

		return fields;
	},
	sightingFields: function(sightingModel) {
		function isClass(speciesClasses) {
			var species = sightingModel.species;
			if(species) {
				return _.some(speciesClasses, c => species.supertaxa.includes('class:' + c));
			}
		}

		var fields = [
			{
				name: "evidence",
				label: "Evidence",
				type: "options",
				multiselect: true,
				options: program.options.nest_box_hollow_inhabitant_evidence,
				style: "checkbox",
				required: true,
				validate: function(f) {
					if(f.value().length == 0) {
						return "At least one type of evidence is required"
					}
				}
			},
			{
				name: "adultCount",
				type: "integer",
				blankIsIndeterminate: true,
				placeholder: "P",
				label: "Adults",
				max: 99999,
				defaultValue: 0,
				hidden: () => !isClass(['Aves', 'Mammalia', 'Reptilia'])
			},
			{
				name: "juvenileCount",
				type: "integer",
				blankIsIndeterminate: true,
				placeholder: "P",
				label: "Juveniles",
				max: 99999,
				defaultValue: 0,
				hidden: () => !isClass(['Mammalia', 'Reptilia'])
			},
			{
				name: "nestlingCount",
				type: "integer",
				blankIsIndeterminate: true,
				placeholder: "P",
				label: "Nestlings",
				max: 99999,
				defaultValue: 0,
				hidden: () => !isClass(['Aves'])
			},
			{
				name: "eggCount",
				type: "integer",
				blankIsIndeterminate: true,
				placeholder: "P",
				label: "Eggs",
				max: 99999,
				defaultValue: 0,
				hidden: () => !isClass(['Aves', 'Reptilia'])
			},
			{
				name: "individualCount",
				type: "integer",
				blankIsIndeterminate: true,
				placeholder: "P",
				label: "Individuals",
				max: 99999,
				defaultValue: 0,
				hidden: () => isClass(['Aves', 'Mammalia', 'Reptilia'])
			},
			{
				name: "allCountsValidation",
				type: "custom",
				validate: () => {
					var directSighting = fields.evidence().map(x => x.code).includes("sighting");

					if(directSighting) {
						var counts = _.values(fields)
							.filter(x => x.field.name.endsWith("Count"))
							.filter(x => !x.field.hidden())
							.map(x => x());
						if(_.all(counts, isZero)) {
							return 'At least one non-zero count is required';
						}
					}
				}
			}
		].map(fieldManager.field);

		fields = util.arrayToMap(fields, function(f) { return f.field.name; });

		return fields;
	},
	programData: function(survey) {
		var fields = program.fields(survey);

		var allSpecies = ko.observable([]);
		birdata.speciesList({ supertaxa: 'all' }).then(allSpecies);

		return {
			fields: fields,
			validations: ko.pureComputed(function() {
				return _.flatten(_.values(fields).map(f => f.field.withDescendants())).map(f => f.value);
			}),
			speciesList: allSpecies
		};
	},
	visibleSharedSiteLayers: () => ["egnb"],
	defaultAreaLayer: (layers) => null,
	multipleDaysEnabled: function() { return false; },
	allSpeciesRecordedEnabled: function() { return false; },
	updateSurveyJSON: function(json, survey) {
		var fields = survey.programData().egnb.fields;
		json.egnb = {};

		_.each(fields, function(f) {
			f.field.save(json.egnb);
		});

		delete json.egnb.site_details_accurate;
		_.each(json.sightings, function(sighting) {
			delete sighting.egnb.all_counts_validation;
		});
	},
	hideSurveyType: true,
	surveyTypeOptions: [],
	forcePrivate: true,
	afterSelect: function(survey) {
		survey.type({
			code: 'nest_box_hollow_survey',
			name: 'Nest box or hollow survey'
		});
		survey.isPrivate(true);
	},
	beforeDeselect: function(survey) {
		survey.type(null);
	},
	loadFromJSON: function(json, fields, survey) {
		var data = json.egnb;

		if(!data) return; //incomplete survey

		data.site_details_accurate = !data.site_details_corrections;

		_.each(fields, function(f) {
			f.field.load(data);
		});
	},
	siteBasedEntry: true,
	recordSiteID: true,
	sightingInstructions: `
	<h4>Instructions</h4>
	<ul><li>
		Record every species (birds or other) found using the nest box and the evidence for the use e.g. direct observation, fur etc.
	</li><li>
		Start by typing the species name in the search bar then click on it and fill out details.
	</li><li>
		Include bird and non-bird species, including those detected from indirect evidence such as fur or feathers.
	</li><li>
		If you are unsure of the exact species, use a generic name such as 'Mammal sp.', 'Lizard sp.' etc.
	</li><li>
		If the species you observed is not listed, use a generic name and supply further details in the 'Notes' field.
	</li><li>
		If you did not observe any evidence of nest box or hollow usage, click 'Next' to skip to the next section.
	</li></ul>
	`,
	updateMap: function(map, survey, areasPromise, locationViewModel) {
		birdata.sharedSiteLayers().then(function(layers) {
			if(survey.program() == program && !survey.surveyPoint()) {
				_.each(layers, function(layer) {
					if(layer.code == "egnb") {
						map.fitBounds(googleMaps.bounds(layer.bounds));
					}
				});
			}
		});
	},
	hasSiteDetailsTab: true
};

export default program;
